import React from 'react';
import { useTranslation } from 'react-i18next';
import Page from 'Core/components/Page';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Card from 'Core/components/FormElements/Card';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import useGetNextStep from 'Modules/easyCredit/hooks/useGetNextStep';
import Loader from 'Core/components/Loader';

const ApplicationApproved = () => {
  document.title = 'Payment Portal - Application Approved';
  useScrollTop();
  const { t } = useTranslation();

  // Fetch payment info to get customer GID and external reference ID
  const { data: paymentInfo, isLoading: isPaymentInfoLoading } = useGetPaymentInfo();

  const customerGid = paymentInfo?.data?.customer_gid || paymentInfo?.customer_gid;
  const externalReferenceId = paymentInfo?.data?.external_reference_id || paymentInfo?.external_reference_id;

  // Fetch next step data to get application details
  const { data: nextStepData, isLoading: isNextStepLoading } = useGetNextStep(
    customerGid,
    externalReferenceId,
    !!(customerGid && externalReferenceId)
  );

  if (isPaymentInfoLoading || isNextStepLoading) {
    return <Loader message={t('status.loading.financingApplication') || 'Loading application...'} />;
  }

  const nextAction = nextStepData?.data?.next_action;
  const approvedAmount = nextStepData?.data?.application?.approved_amount ||
    paymentInfo?.data?.amount_to_pay ||
    paymentInfo?.amount_to_pay ||
    0;

  // Determine approval type based on status
  const isPreApproved = nextAction === 'PRE_APPROVED' || nextAction === 'PRE_APPROVED_LOWER_VALUE';
  const isLowerValue = nextAction === 'PRE_APPROVED_LOWER_VALUE' || nextAction === 'CONSENT_REQUIRED';

  return (
    <Page title={t('easyCredit.page.financing.header')} withStepProgress={false} stickyFooter hideAlert>
      <div className="mt-12 max-w-4xl mx-auto">
        <Card className="bg-gradient-to-br from-white to-gray-50 shadow-xl border-0 overflow-hidden">
          {/* Top accent bar */}
          <div className="h-2 bg-gradient-to-r from-emerald-400 via-green-400 to-emerald-500"></div>

          <div className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Success Icon */}
              <div className="flex-shrink-0 w-40 md:w-56">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 shadow-inner">
                  <div className="w-full aspect-square flex items-center justify-center">
                    <div className="relative">
                      <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <svg className="w-20 h-20 md:w-24 md:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      {/* Decorative circles */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-300 rounded-full opacity-60"></div>
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-green-300 rounded-full opacity-60"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {isPreApproved ? 'Pre-Approved' : 'Approved'}
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                  {t('easyCredit.applicationApproved.title')}
                </h2>

                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                   {t('easyCredit.applicationApproved.content')}
                </p>

                {/* Approved Amount Display */}
                {/* <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200 mb-6">
                  <div className="flex items-center justify-center md:justify-start gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-sm">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium mb-0">
                        {isLowerValue ? 'Approved Amount' : 'Full Amount Approved'}
                      </p>
                      <h3 className="text-3xl font-bold text-gray-900 mb-0">
                        {formatCurrency(approvedAmount, { withDecimals: true })}
                      </h3>
                    </div>
                  </div>

                  {isLowerValue && (
                    <div className="mt-4 pt-4 border-t border-emerald-200">
                      <div className="flex items-start gap-2 text-amber-700">
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm">
                          The approved amount is lower than your original request. Please contact support for more information.
                        </p>
                      </div>
                    </div>
                  )}
                </div> */}

                <div className="flex flex-col sm:flex-row gap-3 items-center justify-center md:justify-start">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">
                      {t('easyCredit.applicationApproved.badgeText')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
};

export default ApplicationApproved;

